import { useEffect, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { apiCall } from "../../config/api";
import { StatCard } from "../../shared/components/StatCard";
import { useCollections } from "./hooks/useCollections";
import { useProviders } from "./hooks/useProviders";
import { useSubmissions } from "./hooks/useSubmissions";

interface QuickLinkCardProps {
  readonly to: string;
  readonly title: string;
  readonly description: string;
  readonly icon: ReactElement;
}

function QuickLinkCard({ to, title, description, icon }: QuickLinkCardProps) {
  return (
    <Link
      to={to}
      className="group bg-neutral-900/0 border border-neutral-800 rounded-lg p-6 hover:border-neutral-700 transition-colors flex flex-col"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white group-hover:text-lumi-accent transition-colors">{title}</h2>
        <div className="w-6 h-6 text-neutral-400 group-hover:text-lumi-accent transition-colors">{icon}</div>
      </div>
      <p className="text-sm text-neutral-400 flex-1">{description}</p>
      <div className="mt-4 text-sm text-lumi-accent group-hover:underline">View {title} →</div>
    </Link>
  );
}

/**
 * Admin Dashboard - Landing page with quick links and key metrics
 * Phase 1: Basic dashboard with navigation cards
 */
// eslint-disable-next-line max-lines-per-function
export function AdminDashboard() {
  // Fetch pending submissions count
  const { pagination: pendingPagination } = useSubmissions({ status: "PENDING", limit: 1 });

  // Fetch published titles count (from titles table, not submissions)
  const [publishedTitlesCount, setPublishedTitlesCount] = useState<number | null>(null);

  // Fetch active providers count
  const { pagination: providersPagination } = useProviders("", 1, 1);

  // Fetch collections count
  const { count: collectionsCount } = useCollections();

  // Load published titles count
  useEffect(() => {
    const loadPublishedTitles = async () => {
      try {
        const response = await apiCall("/v1/titles");
        if (response.ok) {
          const titles = (await response.json()) as unknown[];
          setPublishedTitlesCount(titles.length);
        }
      } catch (error) {
        console.error("Failed to load published titles count:", error);
      }
    };

    void loadPublishedTitles();
  }, []);

  return (
    <div className="space-y-8 py-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-sm text-neutral-400 pb-6">Manage content submissions, providers, and platform collections</p>
      </div>

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLinkCard
          to="/admin/review-queue"
          title="Review Queue"
          description="Review and approve content provider submissions awaiting moderation"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/providers"
          title="Providers"
          description="Manage content provider accounts and view submission history"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/titles"
          title="Title Library"
          description="Browse and edit published titles in the catalog"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/titles/bulk-edit"
          title="Bulk Edit Titles"
          description="Edit title metadata inline, filter by status/genre/year, and take bulk actions"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/collections"
          title="Collections"
          description="Organize platform content into curated collections"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/analytics"
          title="Analytics"
          description="View user behavior insights and content performance metrics"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
        />

        <QuickLinkCard
          to="/admin/audit-log"
          title="Audit Log"
          description="View system audit trail and admin action history"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
        />
      </div>

      {/* Stats Overview - Placeholder for Phase 2 */}
      <div className="border-t border-neutral-800 pt-8">
        <h2 className="text-xl font-semibold text-white mb-4">Platform Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Pending Reviews" value={pendingPagination ? pendingPagination.total.toString() : "..."} />
          <StatCard
            label="Active Providers"
            value={providersPagination ? providersPagination.total.toString() : "..."}
          />
          <StatCard
            label="Published Titles"
            value={publishedTitlesCount === null ? "..." : publishedTitlesCount.toString()}
          />
          <StatCard label="Collections" value={collectionsCount ? collectionsCount.toString() : "..."} />
        </div>
      </div>
    </div>
  );
}
